import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptors,
  withInterceptorsFromDi,
  ɵREQUESTS_CONTRIBUTE_TO_STABILITY as REQUESTS_CONTRIBUTE_TO_STABILITY,
} from '@angular/common/http';
import { isPlatformServer } from '@angular/common';
import {
  APP_ID,
  NgModule,
  Optional,
  PLATFORM_ID,
  REQUEST_CONTEXT,
  TransferState,
} from '@angular/core';
import { SITE_CONTEXT, SITE_CONTEXT_KEY, SiteContext } from '@app/core/site-context';
import { BRAND, BRAND_KEY, PLATFORM_BRAND } from '@app/core/brand';
import { asServerRequestContext } from '../server/render-with-router-capture';
import {
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
  withIncrementalHydration,
} from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { HttpErrorInterceptor } from '@app/core';
import { LoaderModule } from '@app/shared';
import {
  CampaignStoreModule,
  CharityStoreModule,
  DonationCartStoreModule,
  DonationStoreModule,
  ENVIRONMENT,
  FeatureFlagStoreModule,
  GivebriteStoreModule,
  GoCharityStoreModule,
  LiveStoreModule,
  OnboardingStoreModule,
  UserStoreModule,
} from '@app/state';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { CookieModule } from 'ngx-cookie';
import { PixelModule } from 'ngx-multi-pixel';
import { environment } from '@env';
import { liveFragmentMockInterceptor } from './core/live-fragments/live-fragment-mock.interceptor';
import { ssrCookieForwardInterceptor } from './core/interceptors/ssr-cookie-forward.interceptor';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CheckoutRedirectDialogComponent } from './checkout-redirect-dialog.component';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { RedirectDialogComponent } from '@app/features/shared/redirect-dialog/redirect-dialog.component';
import { UiModule } from '@app/ui';
import { QuillModule } from 'ngx-quill';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserAnimationsModule,
    QuillModule.forRoot(),
    AppRoutingModule,
    CookieModule.withOptions(),
    GivebriteStoreModule,
    LiveStoreModule,
    UserStoreModule,
    CharityStoreModule,
    DonationStoreModule,
    FeatureFlagStoreModule,
    OnboardingStoreModule,
    GoCharityStoreModule,
    DonationCartStoreModule,
    CampaignStoreModule,
    RouterModule,
    NgxsFormPluginModule.forRoot(),
    NgxsRouterPluginModule.forRoot(),
    LoaderModule,
    PixelModule.forRoot({ enabled: true, pixelId: [] }),
    UiModule,
    CheckoutRedirectDialogComponent,
    RedirectDialogComponent,
    RecaptchaV3Module,
  ],
  providers: [
    {
      provide: SITE_CONTEXT,
      useFactory: (ts: TransferState, reqCtx: unknown) => {
        // Server-side: reqCtx is the ServerRequestContext passed via
        // angularApp.handle(req, { siteContext, capture }) in server.ts.
        // Components and services see the fully-resolved context without any
        // synchronous TransferState round-trip.
        const serverCtx = asServerRequestContext(reqCtx);
        if (serverCtx) return serverCtx.siteContext;
        // Browser-side: hydrate from TransferState (populated by the
        // AppServerModule APP_INITIALIZER during SSR).
        const ctx = ts.get<SiteContext | null>(SITE_CONTEXT_KEY, null);
        if (ctx) return ctx;
        // Last-resort fallback for non-SSR scenarios (direct browser boot,
        // misconfigured dev).
        return {
          app: 'platform',
          host: typeof window !== 'undefined' ? window.location.host : 'localhost',
          resolvedAt: 'origin',
        } satisfies SiteContext;
      },
      deps: [TransferState, [new Optional(), REQUEST_CONTEXT]],
    },
    {
      provide: BRAND,
      useFactory: (ts: TransferState, reqCtx: unknown) => {
        // Server-side: reqCtx is the ServerRequestContext passed via
        // angularApp.handle(req, { siteContext, brand, capture }) in
        // server.ts. The profile is written into TransferState here so the
        // browser bootstrap below reads the exact same value back rather
        // than regenerating it — a mismatch would surface as NG0500.
        const serverCtx = asServerRequestContext(reqCtx);
        if (serverCtx) {
          ts.set(BRAND_KEY, serverCtx.brand);
          return serverCtx.brand;
        }
        // Browser-side: hydrate from TransferState (populated above during
        // SSR). Falls back to the Givebrite constant for non-SSR scenarios
        // (direct browser boot, misconfigured dev).
        return ts.get(BRAND_KEY, PLATFORM_BRAND);
      },
      deps: [TransferState, [new Optional(), REQUEST_CONTEXT]],
    },
    provideHttpClient(
      withFetch(),
      withInterceptors([ssrCookieForwardInterceptor, liveFragmentMockInterceptor]),
      // Required for class-based interceptors registered via HTTP_INTERCEPTORS —
      // `provideHttpClient` ignores DI interceptors unless this is opted into.
      // HttpErrorInterceptor lives there and adds Authorization / X-Tenant-Domain
      // without it every tenanted API call returns 403 and the
      // failure is swallowed, so pages render blank with no error.
      withInterceptorsFromDi()
    ),
    // Browser-only: don't let in-flight HttpClient requests gate
    // `ApplicationRef.isStable()`. `withEventReplay()` and
    // `withIncrementalHydration()` (below) both wait for stability, so with
    // the default (`true`) a single hung/slow GET (observed: api-dev
    // `charity/statistics` and `donation?...` never responding for unknown
    // origins) keeps the app "unstable" — queued clicks and deferred
    // dehydrated-view cleanup freeze the page for the full 10s NG0506
    // window. On the server the default must stay `true`: SSR serialization
    // waits for stability so the HTTP transfer-state payload is complete.
    // Token is ɵ-prefixed (private API) — re-verify on Angular major bumps.
    {
      provide: REQUESTS_CONTRIBUTE_TO_STABILITY,
      useFactory: (platformId: Object) => isPlatformServer(platformId),
      deps: [PLATFORM_ID],
    },
    { provide: APP_ID, useValue: 'serverApp' },
    { provide: ENVIRONMENT, useValue: environment },
    // `ReCaptchaV3Service` (ng-recaptcha) is NOT `providedIn: 'root'` — it's only
    // registered via `RecaptchaV3Module`'s own `providers:` array (confirmed against
    // the installed package: `RecaptchaV3Module` declares
    // `providers: [ReCaptchaV3Service, RecaptchaLoaderService]` with no root scope).
    // A prior wave's comment here claimed root-scoping and provided only the site-key
    // token, which left `checkout-v2`'s payment card unable to inject the service at
    // all (`NG0201`). Importing the module above (root-level, alongside the legacy
    // `/donate` `CheckoutModule`'s own import of it) plus the site-key token here is
    // what actually makes it resolvable from checkout-v2's lazy standalone routes.
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptcha.v3SiteKey },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    provideClientHydration(
      withEventReplay(),
      withIncrementalHydration(),
      // Serialize SSR HTTP GET responses into the rendered HTML so the
      // browser's first change-detection cycle reads them synchronously
      // instead of re-issuing the request. Prevents the brief "fallback
      // then live" flash on values that depend on async HTTP (e.g. the
      // raised amount via live-fragments).
      withHttpTransferCacheOptions({
        includeHeaders: [],
        includePostRequests: false,
        filter: (req) => req.method === 'GET',
      })
    ),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
